<template>
  <div :class="$options.name">
    <EdroneTooltip v-if="step === -1" :helper="trans._indumeter.commingSoon" position="top">
      <div
        class="tw:flex tw:items-center tw:justify-center tw:mb-3"
        :class="[
          `${$options.name}__emoji`,
          `${$options.name}__emoji--${classEmoji}`,
          `${$options.name}__emoji--${size}`,
        ]"
        style="opacity: 0.4"
        @click="eventGA"
        @mouseenter="eventGA"
      >
        <img :src="emojiImg" />
      </div>
    </EdroneTooltip>
    <EdroneTooltip
      v-else
      type="custom"
      trigger-event="mouseenter click"
      max-width="none"
      position="bottom"
      :offset="[0, 10]"
    >
      <div
        class="tw:flex tw:items-center tw:justify-center tw:mb-3"
        :class="[
          `${$options.name}__emoji`,
          `${$options.name}__emoji--${classEmoji}`,
          `${$options.name}__emoji--${size}`,
        ]"
        @click="eventGA"
        @mouseenter="eventGA"
      >
        <img :src="emojiImg" />
      </div>
      <template #custom>
        <div>
          <EdroneMeterDetail
            :step="step"
            :industry-average="industryAverage"
            :industry-format="industryFormat"
            :steps="steps"
            :metric-name="metricName"
            :is-rate-reversed="isRateReversed"
          />
        </div>
      </template>
    </EdroneTooltip>
  </div>
</template>

<script lang="ts">
import { DTOMetricStep } from "vue-app/api/dto";
import EdroneMeterDetail from "./EdroneMeterDetail.vue";
import EdroneTooltip from "./EdroneTooltip.vue";
import { computed, defineComponent, PropType } from "vue";
import { gtagSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneMeter",
  components: {
    EdroneMeterDetail,
    EdroneTooltip,
  },
  props: {
    /**
     * Show emoji based on step
     */
    step: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * Size of indumeter main emoji
     */
    size: {
      type: String as () => "small" | "big",
      required: false,
      default: "big",
    },
    /**
     * Industry average value
     */
    industryAverage: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * Industry format number or percentage
     */
    industryFormat: {
      type: String,
      required: false,
      default: "NUMBER",
    },
    /**
     * Steps for industry average
     */
    steps: {
      type: Array as PropType<DTOMetricStep[]>,
      required: false,
      default: () => [
        { step: 0, minValue: 0, maxValue: 0 },
        { step: 1, minValue: 0, maxValue: 0 },
        { step: 2, minValue: 0, maxValue: 0 },
        { step: 3, minValue: 0, maxValue: 0 },
        { step: 4, minValue: 0, maxValue: 0 },
      ],
    },
    /**
     * Type of metrics whether it is OR, CTR, etc. Needed for GA
     */
    metricName: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * If true, the rating is presented from the highest value to the lowest.
     */
    isRateReversed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const emojiImg = computed(() => {
      switch (props.step) {
        case -1:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/commingSoon.png";
        case 0:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/bottom.png";
        case 1:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/belowAvg.png";
        case 2:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/average.png";
        case 3:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/overAvg.png";
        case 4:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/top.png";
        default:
          return "https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/noData.png";
      }
    });

    const classEmoji = computed(() => {
      switch (props.step) {
        case -1:
          return "commingSoon";
        case 0:
          return "bottom";
        case 1:
          return "belowAvg";
        case 2:
          return "average";
        case 3:
          return "overAvg";
        case 4:
          return "top";
        default:
          return "noData";
      }
    });

    function eventGA() {
      gtagSend({
        event: "button_icon_click",
        component_type: "",
        component_variant: "",
        component_icon_name: "emoji",
        component_state: "active",
        component_label: "edrone",
        functionality_group: "newsletter",
        functionality: "dashboard",
        view: "",
        text: props.metricName,
      });
    }

    return {
      emojiImg,
      classEmoji,
      eventGA,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneMeter {
  position: relative;
  width: max-content;
}

.EdroneMeter__emoji {
  width: 36px;
  height: 36px;
  border: 2px #00c672 solid;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
}

.EdroneMeter__emoji img {
  width: 26px;
}

.EdroneMeter__emoji--small {
  width: 28px;
  height: 28px;
}

.EdroneMeter__emoji--small img {
  width: 20px;
}

.EdroneMeter__emoji--bottom {
  @apply tw:border-red-500 tw:bg-red-100;
}

.EdroneMeter__emoji--belowAvg {
  @apply tw:border-yellow-500 tw:bg-yellow-100;
}

.EdroneMeter__emoji--average,
.EdroneMeter__emoji--overAvg,
.EdroneMeter__emoji--top {
  @apply tw:border-green-500 tw:bg-green-100;
}

.EdroneMeter__emoji--noData {
  @apply tw:border-blue-100 tw:bg-blue-000;
}

.EdroneMeter__emoji--commingSoon {
  @apply tw:border-default-200 tw:bg-default-100;
}
</style>
